<template>
    <div class="balance-container vue-component-container">
       <div class="list-actions">
         <button class="btnGcn custom-btnGcn" @click="openAddModal">
             <span class="icon material-icons-outlined" style="font-size: 1.2em; vertical-align: middle; margin-right: 4px;">add_circle_outline</span> Añadir Registro de Balance
         </button>
          <!-- Other actions if needed -->
      </div>
  
       <div v-if="loading" class="loading-indicator-gcn">Cargando balances...</div>
       <div v-if="error" class="error-message-gcn">{{ error }}</div>
  
       <div class="table-responsive" v-if="!loading && !error && balances.length > 0">
          <table class="table data-table balance-table">
               <thead>
                  <tr>
                      <th>Fecha</th>
                      <th class="number-cell">Efectivo (Cash)</th>
                      <th class="number-cell">Capital Ficticio</th>
                      <th class="number-cell">Capital Real</th>
                      <th>Tipo</th>
                      <th>Descripción</th>
                      <th class="number-cell">Variación Capital</th>
                      <th>Acción</th>
                  </tr>
              </thead>
               <tbody>
                  <tr v-for="(bal, index) in balances" :key="bal.date + '-' + bal.transaction_type"> <!-- Composite key -->
                       <td>{{ formatDate(bal.date) }}</td>
                       <td class="number-cell">{{ formatCurrencyWithCommas(bal.cash, portfolioCurrency) }}</td>
                       <td class="number-cell">{{ formatCurrencyWithCommas(bal.fictitious_capital, portfolioCurrency) }}</td>
                       <td class="number-cell">{{ formatCurrencyWithCommas(bal.real_capital, portfolioCurrency) }}</td>
                       <td>{{ translateTransactionType(bal.transaction_type_name) }}</td>
                       <td>{{ bal.description }}</td>
                        <td :class="getBalanceTextClass(bal) + ' number-cell'">
                           {{ (bal.transaction_type === 1 || bal.transaction_type === 2) ? formatCurrencyWithCommas(bal.variation_capital, portfolioCurrency) : '-' }}
                       </td>
                       <td class="options-cell">
                           <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-danger"
                                   @click="confirmDelete(bal)"
                                   :title="index === balances.length - 1 ? 'El primer registro no se puede eliminar' : 'Eliminar Registro'"
                                   :disabled="isSubmitting || index === balances.length - 1"
                                   :class="{ 'disabled': index === balances.length - 1 }"> <!-- Visually indicate disabled state -->
                              <span v-if="isSubmitting && deletingDate === bal.date">...</span>
                              <span v-else class="material-icons-outlined">delete</span>
                           </button>
                       </td>
                   </tr>
              </tbody>
           </table>
       </div>
        <div v-else-if="!loading && !error && balances.length === 0">
          <p>No hay registros de balance para esta cartera.</p>
       </div>
  
        <!-- Add/Edit Balance Modal -->
       <Modal :show="showAddModal" title="Añadir Registro de Balance" @close="closeAddModal" size="md">
           <template #body>
                <BalanceForm
                   ref="balanceForm"
                   :portfolio="selectedPortfolioData"
                   :is-submitting="isSubmitting"
                   @submit="handleAddBalance"
                   @error="setFormError"
                   @calculation-loading="handleCalculationLoading"
                />
           </template>
           <template #footer>
               <button class="btnGcn btnGcn-secondary" @click="closeAddModal">Cancelar</button>
               <button class="btnGcn btnGcn-primary" @click="$refs.balanceForm.submitForm()" :disabled="isSubmitting || isCalculating">
                   {{ isSubmitting ? 'Guardando...' : isCalculating ? 'Calculando...' : 'Guardar Registro' }}
               </button>
           </template>
       </Modal>
  
    </div>
  </template>
  
  <script>
  import apiService, { parseDate, formatCurrency } from './apiService';
  import Modal from './Modal.vue'; // Adjust path if needed
  import BalanceForm from './BalanceForm.vue'; // Adjust path if needed
  
  export default {
    name: 'Balance',
    components: {
        Modal,
        BalanceForm
    },
    props: {
      portfolioId: { type: String, required: true },
    },
    data() {
      return {
        loading: false,
        error: null,
        isSubmitting: false,
        isCalculating: false, // Track calculation loading state
        balances: [],
        portfolioCurrency: 'EUR', // Default, needs update
        selectedPortfolioData: { idc: this.portfolioId, name: '', currency: 'EUR' }, // Pass portfolio details to form
        showAddModal: false,
        deletingDate: null, // Track which row is being deleted
      };
    },
     methods: {
       // --- Anglo Number Formatting Helpers ---
       formatNumberAnglo(value, decimals = 2) {
         if (value === null || value === undefined || value === '') return '-';
         
         // Convert to string first, then to number to handle various input formats
         let stringValue = String(value).trim();
         // Remove any existing formatting (commas, currency symbols, etc.)
         stringValue = stringValue.replace(/[^\d.-]/g, '');
         
         const num = Number(stringValue);
         if (isNaN(num)) {
           console.warn('formatNumberAnglo: Invalid value:', value);
           return '-';
         }
         
         const absValue = Math.abs(num);
         const parts = absValue.toFixed(decimals).split('.');
         const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
         const decimalPart = parts[1];
         return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
       },
       formatCurrency(value, currency = 'EUR') {
         return this.formatCurrencyWithCommas(value, currency);
       },
       formatCurrencyWithCommas(value, currency = 'EUR') {
           if (value === null || value === undefined || value === '') return '-';
           
           const num = typeof value === 'number' ? value : Number(String(value).trim().replace(/[^\d.-]/g, ''));
           if (isNaN(num)) {
               console.warn('formatCurrencyWithCommas: Invalid value:', value);
               return '-';
           }
           
           const isNegative = num < 0;
           const formattedNumber = this.formatNumberAnglo(Math.abs(num), 2);
           const currencySymbol = currency === 'EUR' ? '€' : currency;
           
           return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
       },
       formatDate(dateString) {
          const date = parseDate(dateString); // Handles Y-m-d from DB
          return date ? date.toLocaleDateString('es-ES') : dateString;
      },
       translateTransactionType(transactionTypeName) {
           // Translation map for transaction type names
           const translations = {
               'Daily balance': 'Balance diario',
               'Increase capital': 'Aumento de capital',
               'Decrease capital': 'Disminución de capital',
               'Deposit': 'Depósito',
               'Withdrawal': 'Retiro',
               'Investment': 'Inversión',
               'Dividend': 'Dividendo',
               'Fee': 'Comisión',
               'Transfer': 'Transferencia',
               'Buy': 'Compra',
               'Sell': 'Venta'
           };
           return translations[transactionTypeName] || transactionTypeName;
       },
       getBalanceTextClass(balance) {
           if (balance.transaction_type === 1) return 'text-success'; // Increase
           if (balance.transaction_type === 2) return 'text-danger'; // Decrease
           return '';
       },
       async fetchBalanceData() {
           if (!this.portfolioId) return;
           this.loading = true; this.error = null;
           try {
               // Fetch both balances and portfolio details concurrently
               const [balanceList, portfolioDetails] = await Promise.all([
                   apiService.listDailyBalances(this.portfolioId),
                   apiService.getPortfolio(this.portfolioId) // Fetch details to get currency etc.
               ]);
  
               this.balances = balanceList;
               if (portfolioDetails) {
                   this.portfolioCurrency = portfolioDetails.currency || 'EUR';
                    // Update data passed to the form
                   this.selectedPortfolioData = {
                       idc: portfolioDetails.idc,
                       name: portfolioDetails.name,
                       currency: portfolioDetails.currency
                   };
               } else {
                    this.portfolioCurrency = 'EUR'; // Fallback
                    console.warn("Could not fetch portfolio details for Balance tab.");
                     this.selectedPortfolioData = { idc: this.portfolioId, name: 'Desconocida', currency: 'EUR' };
               }
  
           } catch (err) {
               this.error = `Error cargando balances: ${err.message}`;
               this.balances = [];
           } finally {
               this.loading = false;
           }
       },
       openAddModal() { this.showAddModal = true; },
       closeAddModal() { 
           this.showAddModal = false; 
           this.isCalculating = false; // Reset calculation state when modal closes
       },
       setFormError(message) {
           this.$vueOnToast.pop('error', message);
       },
  
       async handleAddBalance(formData) {
           if (this.isSubmitting) return;
           this.isSubmitting = true;
           try {
                // API returns updated list
               const updatedList = await apiService.addDailyBalanceOrVariation(this.portfolioId, formData);
               this.balances = updatedList;
               this.closeAddModal();
           } catch (err) { this.setFormError(err.message || 'No se ha podido guardar el registro. Inténtalo de nuevo.'); }
           finally { this.isSubmitting = false; }
       },
  
       async confirmDelete(balance) {
            // Date from DB is likely Y-m-d
            const dateToDelete = balance.date;
            if (confirm(`¿Está seguro de que desea eliminar el registro de balance del ${this.formatDate(dateToDelete)}?`)) {
               this.isSubmitting = true; this.error = null; this.deletingDate = dateToDelete;
               try {
                   // API returns updated list
                   const updatedList = await apiService.deleteDailyBalance(this.portfolioId, dateToDelete);
                   this.balances = updatedList;
               } catch (err) { this.error = `Error al eliminar registro: ${err.message}`; }
               finally { this.isSubmitting = false; this.deletingDate = null;}
           }
       },
       handleCalculationLoading(isLoading) {
           this.isCalculating = isLoading;
       }
  
     },
     watch: {
      portfolioId: {
        immediate: true,
        handler(newId) {
          if (newId) {
               // Update portfolio data passed to form when ID changes
               this.selectedPortfolioData.idc = newId;
              this.fetchBalanceData();
          } else {
               this.balances = []; this.error = null;
                this.selectedPortfolioData = { idc: null, name: '', currency: 'EUR' };
                this.portfolioCurrency = 'EUR';
          }
        },
      },
    },
  };
  </script>
  
  <style scoped>
  /* --- Colors from App Theme --- */
  .balance-container.vue-component-container {
    padding: 0 10px;
    font-size: 0.92rem;
    color: var(--color-text-primary);
  }

  .material-icons-outlined {
    font-size: 16px;
    vertical-align: middle;
    line-height: 1;
    margin-right: 3px;
  }

  .btnGcn-sm .material-icons-outlined, 
  .options-cell .material-icons-outlined {
    font-size: 18px;
    margin-right: 0;
  }

  /* --- Action Bar Styling --- */
  .list-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 8px 12px;
    border-radius: 5px;
    background: linear-gradient(to right, var(--color-active-surface), var(--color-background));
    flex-wrap: wrap;
    gap: 8px;
  }

  /* --- Custom Button Styles --- */
  .custom-btnGcn {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-highlight);
    transition: all 0.2s ease;
    padding: 6px 12px;
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .custom-btnGcn:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px var(--color-outline);
  }

  .custom-btnGcn:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px var(--color-outline);
  }

  .custom-btnGcn:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  .custom-btnGcn .material-icons-outlined {
    margin-right: 5px;
    font-size: 1.2em;
  }

  .btnGcn-secondary {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    font-size: 0.85em;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-secondary:hover {
    background-color: var(--color-active-surface);
    border-color: var(--color-text-secondary);
  }

  .btnGcn-secondary:active {
    background-color: var(--color-outline);
  }

  .btnGcn-secondary:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-primary {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-highlight);
    font-size: 0.85em;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-primary:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-primary:active {
    transform: translateY(0);
    opacity: 0.9;
  }

  .btnGcn-primary:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  /* --- Loading and Error Messages --- */
  .loading-indicator-gcn, .error-message-gcn {
    color: var(--color-text-secondary);
    text-align: center;
    padding: 15px;
    font-size: 0.9em;
    border-radius: 4px;
    margin-bottom: 12px;
  }

  .error-message-gcn {
    color: var(--color-error);
    background-color: var(--color-active-surface);
  }

  /* --- Table Styling --- */
  .table-responsive {
    overflow-x: auto;
    margin-top: 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px var(--color-outline);
    margin-bottom: 20px;
    max-height: 70vh; /* Limit height to enable scrolling */
    overflow-y: auto; /* Enable vertical scrolling */
  }

  .table.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85em;
  }

  .data-table th, .data-table td {
    border: 1px solid var(--color-outline);
    padding: 6px 8px;
    text-align: center;
    vertical-align: middle;
    color: var(--color-text-primary);
    line-height: 1.3;
  }

  .data-table th {
    background-color: var(--color-surface);
    font-weight: 600;
    white-space: nowrap;
    color: var(--color-text-secondary);
    text-align: center;
    font-size: 0.9em;
    padding: 6px 8px;
    position: sticky; /* Make header sticky */
    top: 0; /* Stick to top when scrolling */
    z-index: 10; /* Ensure header stays above content */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for better visibility */
    border-bottom: 2px solid var(--color-outline); /* Stronger border for sticky header */
  }

  /* Removed alternating row colors per user preference */
  /* .data-table tbody tr:nth-child(even) {
    background-color: var(--color-active-surface);
  } */

  .data-table tbody tr:hover {
    background-color: var(--color-active-surface);
  }

  .balance-table th, .balance-table td {
    font-size: 0.9em;
    padding: 7px 10px;
    vertical-align: middle;
    text-align: center;
  }

  /* --- Options Cell Styling --- */
  .options-cell {
    white-space: nowrap;
    text-align: center;
  }

  .options-cell .btnGcn-link {
    padding: 0;
    width: 24px;
    height: 24px;
    font-size: 16px;
    text-decoration: none;
    vertical-align: middle;
    color: var(--color-text-secondary);
    background-color: transparent;
    border: none;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 1px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .options-cell .btnGcn-link:hover {
    color: var(--color-text-primary);
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .options-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .options-cell .btnGcn-link:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .options-cell .btnGcn-link.btnGcn-danger {
    color: var(--color-error);
  }

  .options-cell .btnGcn-link.btnGcn-danger:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-error);
  }

  .options-cell .btnGcn-link.disabled {
    color: var(--color-text-inactive);
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.3;
    transition: none;
  }

  .options-cell .btnGcn-link.disabled:hover,
  .options-cell .btnGcn-link.disabled:focus,
  .options-cell .btnGcn-link.disabled:active {
    background-color: transparent !important;
    color: var(--color-text-inactive) !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.3 !important;
  }

  /* --- Text Classes for Balance Variations --- */
  .text-success {
    color: var(--color-success);
    font-weight: 500;
  }

  .text-danger {
    color: var(--color-error);
    font-weight: 500;
  }

  /* --- Numeric Cell Alignment --- */
  .data-table td.number-cell,
  .data-table th.number-cell {
    text-align: center;
    padding-right: 8px;
  }

  .data-table th.number-cell {
    text-align: center;
  }

  /* --- Disabled States for Buttons --- */
  .custom-btnGcn:disabled,
  .btnGcn-secondary:disabled,
  .btnGcn-primary:disabled {
    background-color: var(--color-surface);
    color: var(--color-text-inactive);
    border-color: var(--color-outline);
    cursor: not-allowed;
    opacity: 0.5;
    transform: none !important;
    box-shadow: none !important;
    pointer-events: none;
  }

  /* --- Responsive Design --- */
  @media (max-width: 768px) {
    .data-table th, .data-table td {
      padding: 4px 6px;
      font-size: 0.8em;
    }

    .balance-table th, .balance-table td {
      font-size: 0.8em;
      padding: 5px 8px;
    }

    .options-cell .btnGcn-link {
      width: 22px;
      height: 22px;
      font-size: 14px;
    }

    .list-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .list-actions .btnGcn {
      width: 100%;
      margin-bottom: 5px;
    }
    
    /* Adjust sticky header behavior on mobile */
    .data-table th {
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    /* Reduce max height on mobile for better usability */
    .table-responsive {
      max-height: 60vh;
    }
  }

  @media (max-width: 992px) {
    .data-table th, .data-table td {
      padding: 6px 8px;
      font-size: 0.8em;
    }

    .balance-table th, .balance-table td {
      font-size: 0.85em;
      padding: 6px 9px;
    }
  }
  </style>